import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchFeatureFlags,
  selectFeatureFlags,
  selectFirmDataEnabled,
  selectUser,
  shouldRequestFeatureFlags,
} from '@/store/features/userinfo/userinfoSlice';

/**
 * ENG-2695 — single source of truth for "is this advisor's firm migrated to
 * firm-data as canonical storage?".
 *
 * It reads the existing backend-owned feature profile (`ClientFeaturesRecord`)
 * and the authenticated firm-data access context. This is the ONLY place the
 * frontend resolves firm-data-enabled, so if the backend later exposes it
 * differently (e.g. a dedicated tenant feature flag or a field on the profile
 * read), only this hook changes — no UI call sites are affected.
 *
 * Returns `loading: true` until the feature profile is fetched so callers can
 * avoid flashing the wrong (legacy vs new) edit path.
 */
// Dev-only escape hatch so the new firm-data UI can be previewed locally without
// the backend flag. In a dev build only, `localStorage.forceFirmDataEnabled = "true"`
// forces enabled. Never honored in production builds.
const isDevForceFirmDataEnabled = (): boolean => {
  try {
    return (
      import.meta.env?.DEV === true &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('forceFirmDataEnabled') === 'true'
    );
  } catch {
    return false;
  }
};

export const useFirmDataEnabled = (): { enabled: boolean; loading: boolean; error: boolean } => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const enabled = useAppSelector(selectFirmDataEnabled);

  useEffect(() => {
    if (shouldRequestFeatureFlags(user, featureFlags)) {
      dispatch(fetchFeatureFlags());
    }
  }, [user, dispatch, featureFlags]);

  const forced = isDevForceFirmDataEnabled();

  return {
    enabled: forced || enabled,
    // Unknown until the feature profile resolves. Treat "not yet fetched" as
    // loading (not resolved-disabled) so callers don't briefly render the legacy
    // path for a firm-data firm during the window before the fetch dispatches.
    // Resolves to false once the profile arrives or the fetch errors.
    loading:
      forced || enabled || !user
        ? false
        : featureFlags.profile === undefined && !featureFlags.error,
    error: featureFlags.error,
  };
};

export default useFirmDataEnabled;
